import React, { useEffect, useRef, useState } from "react";
import { Button, Modal, message } from "antd";

const HANDLE_SIZE = 14;
const MIN_CROP = 40;

const HANDLES = [
  { key: "nw", cursor: "nwse-resize", style: { left: -HANDLE_SIZE / 2, top: -HANDLE_SIZE / 2 } },
  { key: "ne", cursor: "nesw-resize", style: { right: -HANDLE_SIZE / 2, top: -HANDLE_SIZE / 2 } },
  { key: "sw", cursor: "nesw-resize", style: { left: -HANDLE_SIZE / 2, bottom: -HANDLE_SIZE / 2 } },
  { key: "se", cursor: "nwse-resize", style: { right: -HANDLE_SIZE / 2, bottom: -HANDLE_SIZE / 2 } },
];

const handleBaseStyle = {
  position: "absolute",
  width: HANDLE_SIZE,
  height: HANDLE_SIZE,
  background: "#fff",
  border: "2px solid #1890ff",
  borderRadius: 2,
  boxSizing: "border-box",
  zIndex: 2,
};

const clampCrop = (next, imageSize) => {
  if (!imageSize.width || !imageSize.height) return next;

  let { x, y, width, height } = next;

  width = Math.max(MIN_CROP, Math.min(width, imageSize.width));
  height = Math.max(MIN_CROP, Math.min(height, imageSize.height));

  x = Math.min(Math.max(0, x), imageSize.width - width);
  y = Math.min(Math.max(0, y), imageSize.height - height);

  return { x, y, width, height };
};

const getCroppedImageFile = (image, crop, fileName, mimeType) =>
  new Promise((resolve) => {
    const scaleX = image.naturalWidth / image.width;
    const scaleY = image.naturalHeight / image.height;
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");

    canvas.width = Math.round(crop.width * scaleX);
    canvas.height = Math.round(crop.height * scaleY);

    ctx.drawImage(
      image,
      crop.x * scaleX,
      crop.y * scaleY,
      crop.width * scaleX,
      crop.height * scaleY,
      0,
      0,
      canvas.width,
      canvas.height
    );

    canvas.toBlob(
      (blob) => {
        if (!blob) {
          resolve(null);
          return;
        }
        resolve(new File([blob], fileName, { type: mimeType, lastModified: Date.now() }));
      },
      mimeType,
      0.92
    );
  });

export const AccusedImageCropModal = ({
  visible,
  imageSrc,
  fileName,
  mimeType,
  onCancel,
  onApply,
  onUseOriginal,
}) => {
  const imageRef = useRef(null);
  const dragRef = useRef(null);
  const [crop, setCrop] = useState(null);
  const [imageSize, setImageSize] = useState({ width: 0, height: 0 });
  const [applying, setApplying] = useState(false);

  const resetState = () => {
    setCrop(null);
    setImageSize({ width: 0, height: 0 });
    setApplying(false);
    dragRef.current = null;
  };

  useEffect(() => {
    if (!visible) {
      resetState();
    }
  }, [visible]);

  useEffect(() => {
    const onMove = (event) => {
      const drag = dragRef.current;
      if (!drag) return;

      const dx = event.clientX - drag.startX;
      const dy = event.clientY - drag.startY;
      const start = drag.startCrop;
      let next = { ...start };

      if (drag.type === "move") {
        next = {
          x: start.x + dx,
          y: start.y + dy,
          width: start.width,
          height: start.height,
        };
      } else {
        if (drag.type.includes("w")) {
          const newX = Math.max(
            0,
            Math.min(start.x + dx, start.x + start.width - MIN_CROP)
          );
          next.x = newX;
          next.width = start.width + (start.x - newX);
        }
        if (drag.type.includes("e")) {
          next.width = Math.max(
            MIN_CROP,
            Math.min(start.width + dx, drag.imageSize.width - start.x)
          );
        }
        if (drag.type.includes("n")) {
          const newY = Math.max(
            0,
            Math.min(start.y + dy, start.y + start.height - MIN_CROP)
          );
          next.y = newY;
          next.height = start.height + (start.y - newY);
        }
        if (drag.type.includes("s")) {
          next.height = Math.max(
            MIN_CROP,
            Math.min(start.height + dy, drag.imageSize.height - start.y)
          );
        }
      }

      setCrop(clampCrop(next, drag.imageSize));
    };

    const onUp = () => {
      dragRef.current = null;
    };

    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    };
  }, []);

  const handleImageLoad = (event) => {
    const { width, height } = event.target;
    const insetX = Math.max(16, width * 0.08);
    const insetY = Math.max(16, height * 0.08);
    setImageSize({ width, height });
    setCrop(
      clampCrop(
        {
          x: insetX,
          y: insetY,
          width: width - insetX * 2,
          height: height - insetY * 2,
        },
        { width, height }
      )
    );
  };

  const startDrag = (type) => (event) => {
    event.preventDefault();
    event.stopPropagation();
    if (!crop) return;
    dragRef.current = {
      type,
      startX: event.clientX,
      startY: event.clientY,
      startCrop: { ...crop },
      imageSize,
    };
  };

  const handleApply = async () => {
    if (!crop || !imageRef.current) return;
    setApplying(true);
    try {
      const croppedFile = await getCroppedImageFile(
        imageRef.current,
        crop,
        fileName,
        mimeType || "image/jpeg"
      );
      if (!croppedFile) {
        message.error("Unable to crop image. Please try again.");
        return;
      }
      onApply?.(croppedFile);
    } finally {
      setApplying(false);
    }
  };

  return (
    <Modal
      title="Crop accused photo"
      visible={visible}
      onCancel={onCancel}
      width={640}
      destroyOnClose
      maskClosable={false}
      footer={[
        <Button key="cancel" onClick={onCancel}>
          Cancel
        </Button>,
        <Button key="original" onClick={onUseOriginal}>
          Use original
        </Button>,
        <Button key="apply" type="primary" loading={applying} onClick={handleApply}>
          Crop & Upload
        </Button>,
      ]}
    >
      <p style={{ marginBottom: 12, color: "rgba(0,0,0,0.65)" }}>
        Drag the blue corners to crop the image, or drag inside the box to move it.
      </p>
      <div style={{ textAlign: "center", background: "#111", borderRadius: 4, padding: 8 }}>
        <div
          style={{
            position: "relative",
            display: "inline-block",
            maxWidth: "100%",
            overflow: "hidden",
            lineHeight: 0,
            verticalAlign: "top",
          }}
        >
          {imageSrc ? (
            <img
              ref={imageRef}
              src={imageSrc}
              alt="Crop accused"
              onLoad={handleImageLoad}
              draggable={false}
              style={{
                maxWidth: "100%",
                maxHeight: 400,
                display: "block",
                userSelect: "none",
              }}
            />
          ) : null}
          {crop ? (
            <div
              onMouseDown={startDrag("move")}
              style={{
                position: "absolute",
                left: crop.x,
                top: crop.y,
                width: crop.width,
                height: crop.height,
                border: "1px solid #fff",
                boxShadow: "0 0 0 9999px rgba(0, 0, 0, 0.45)",
                cursor: "move",
                boxSizing: "border-box",
              }}
            >
              {HANDLES.map((handle) => (
                <span
                  key={handle.key}
                  onMouseDown={startDrag(handle.key)}
                  style={{
                    ...handleBaseStyle,
                    ...handle.style,
                    cursor: handle.cursor,
                  }}
                />
              ))}
            </div>
          ) : null}
        </div>
      </div>
    </Modal>
  );
};
